import * as c from '../constants'

// Flujo oficial de reCAPTCHA v3: el script se carga con ?render=<clave> y el token
// se pide con grecaptcha.execute(<clave>, { action }).
//
// IMPORTANTE: no usar grecaptcha.render() con size:'invisible' (renderizado explícito).
// Ese es el flujo de reCAPTCHA v2 y devuelve un token distinto (~1.000 caracteres) que
// siteverify rechaza. El flujo v3 devuelve tokens de más de 2.000 caracteres.
// Firebase App Check (./firebase.js) usa internamente ese renderizado explícito, por eso
// aquí se carga el script con su propia etiqueta y no se reutiliza la de Firebase.
const SCRIPT_ID = 'recaptcha-v3-script'
const TIMEOUT_MS = 10000

let loadPromise = null

function loadScript() {
  if (!loadPromise) {
    loadPromise = new Promise((resolve, reject) => {
      const onReady = () => window.grecaptcha.ready(() => resolve())

      let script = document.getElementById(SCRIPT_ID)
      if (script) {
        if (window.grecaptcha && window.grecaptcha.execute) return onReady()
      } else {
        script = document.createElement('script')
        script.id = SCRIPT_ID
        script.src = `https://www.google.com/recaptcha/api.js?render=${c.RECAPTCHA_SITE_KEY}`
        script.async = true
        document.head.appendChild(script)
      }

      script.addEventListener('load', onReady)
      script.addEventListener('error', () => {
        loadPromise = null
        reject(new Error('No se pudo cargar reCAPTCHA'))
      })
    })
  }
  return loadPromise
}

function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('Tiempo de espera de reCAPTCHA agotado')), ms)),
  ])
}

/**
 *
 * @description Carga el script de reCAPTCHA. Conviene llamarlo al montar el formulario:
 * así el script ya está listo al enviar y Google dispone de más contexto para puntuar
 * la sesión (un token pedido justo después de cargar obtiene peor score).
 *
 */
export function initRecaptcha() {
  if (!c.RECAPTCHA_SITE_KEY) return
  loadScript().catch(() => {})
}

/**
 *
 * @param {string} action Nombre de la acción que el backend debe validar
 * @returns {Promise<string|null>} Token de reCAPTCHA v3, o null si no hay clave configurada
 * o no se ha podido obtener (script bloqueado, sin conexión con Google...)
 * @description Obtiene un token de reCAPTCHA v3. Los tokens caducan a los 2 minutos
 * y solo se pueden validar una vez, por lo que hay que pedir uno nuevo en cada envío.
 * Si falla no se bloquea la petición: es el backend quien decide si la rechaza.
 *
 */
export async function getRecaptchaToken(action) {
  if (!c.RECAPTCHA_SITE_KEY) return null
  try {
    await withTimeout(loadScript(), TIMEOUT_MS)
    return await withTimeout(window.grecaptcha.execute(c.RECAPTCHA_SITE_KEY, { action }), TIMEOUT_MS)
  } catch (e) {
    return null
  }
}
